import React from 'react';
import PropTypes from 'prop-types';

import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { truncate } from 'lodash';

import {
  bodyText,
  borderGray,
  borderLightGray,
  ghostWhite,
  shadowDark,
  shadowLight,
  slateGrey,
  softAmberLight,
  softPurple,
  vividOrange,
  warmBlue,
  warmGray,
  white,
} from 'lib/css/colors';
import { DISPLAY_NAME_LENGTH } from 'components/codify_db/constants';
import { formatGMV } from 'lib/utils/FormatUtils';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import { ProductType } from 'lib/enums/ProductType';
import { titleCase } from 'lib/utils/StringProcess';

// A distributor's DP product == a vendor product (GLOBAL_PRODUCT_VENDOR). It carries a vendor SKU
// (never a manufacturer/UPB SKU) and its annualized GMV, the headline metric here. When the product
// is mapped, we also surface the mapped manufacturer-product code (mfr_sku), tagging UPB-generated
// codes so they're easy to spot.
function DistributorProductCard({ product }) {
  const productImageUrl = product.thumbnail_url ?? placeholderProductImage;
  const hasGmv = product.product_gmv && product.product_gmv > 0;

  // "Brand by Manufacturer" display. When the product has no brand we show nothing — never the
  // distributor/vendor name, which is not a brand.
  const brandByManufacturer = product.brand_name
    ? product.manufacturer_name
      ? `${product.brand_name} by ${product.manufacturer_name}`
      : product.brand_name
    : null;

  // A UPB SKU is an internally-generated placeholder code (prefix "UPB"), not a real mfr code.
  const isUpbSku = (product.mfr_sku || '').toUpperCase().startsWith('UPB');

  // DP product detail = the global product detail page scoped to this vendor product
  // (productType 'distributor'), matching how vendor SKUs link elsewhere (Brand SKU Mapper).
  const pdpLink =
    product.global_product_id && product.id
      ? `/global-product-detail/${product.global_product_id}` +
        `/${product.id}/${ProductType.DISTRIBUTOR}`
      : null;

  const content = (
    <>
      <div className={css(styles.productImageContainer)}>
        <Card.Img
          className={css(styles.productImage)}
          src={productImageUrl}
          alt={product.name}
          onError={e => {
            e.target.onerror = null;
            e.target.src = placeholderProductImage;
          }}
        />
      </div>
      <div className={css(styles.productDetails)}>
        <div className={css(styles.gmvValue, !hasGmv && styles.gmvNoData)}>
          {hasGmv ? formatGMV(product.product_gmv) : 'No GMV'}
        </div>
        <div className={css(styles.displayName)}>
          {titleCase(truncate(product.name || '', { length: DISPLAY_NAME_LENGTH, omission: '' }))}
          {(product.name || '').length > DISPLAY_NAME_LENGTH && (
            <OverlayTrigger overlay={<Tooltip>{titleCase(product.name)}</Tooltip>}>
              <span>...</span>
            </OverlayTrigger>
          )}
        </div>
        {brandByManufacturer && <div className={css(styles.brandName)}>{brandByManufacturer}</div>}
        <div className={css(styles.productMeta)}>
          {product.pack_size && <div className={css(styles.metaItem)}>{product.pack_size}</div>}
          {product.vendor_sku && (
            <div className={css(styles.metaItem)}>Vendor SKU: {product.vendor_sku}</div>
          )}
        </div>
        {product.mfr_sku && (
          <div className={css(styles.mfrSkuRow)}>
            <span className={css(styles.mfrSkuItem)}>MFR SKU: {product.mfr_sku}</span>
            {isUpbSku && (
              <OverlayTrigger
                overlay={<Tooltip>UPB-generated SKU (not a manufacturer code)</Tooltip>}
              >
                <span className={css(styles.upbTag)}>UPB</span>
              </OverlayTrigger>
            )}
          </div>
        )}
      </div>
    </>
  );

  return (
    <Card className={classNames('position-relative', css(styles.card))}>
      {pdpLink ? (
        <a
          href={pdpLink}
          target="_blank"
          rel="noopener noreferrer"
          className={css(styles.cardLink)}
        >
          {content}
        </a>
      ) : (
        content
      )}
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    width: 200,
    borderRadius: 14,
    margin: '10px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: `1px solid ${borderGray}`,
    boxShadow: `0 4px 12px ${shadowLight}`,
    overflow: 'hidden',
    backgroundColor: white,
    ':hover': {
      transform: 'translateY(-6px)',
      boxShadow: `0 16px 32px ${shadowDark}`,
      border: `1px solid ${warmBlue}`,
    },
  },
  cardLink: {
    display: 'block',
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'none',
      color: 'inherit',
    },
  },
  productImageContainer: {
    position: 'relative',
    width: '100%',
    height: '170px',
    backgroundColor: ghostWhite,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderBottom: `1px solid ${borderLightGray}`,
  },
  productImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    padding: '14px',
  },
  productDetails: {
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  gmvValue: {
    fontSize: '18px',
    fontWeight: 800,
    color: softPurple,
    letterSpacing: '-0.3px',
  },
  gmvNoData: {
    color: slateGrey,
    fontWeight: 600,
    fontSize: '14px',
  },
  displayName: {
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '1.3',
    color: bodyText,
    minHeight: '34px',
  },
  brandName: {
    fontSize: '12px',
    color: warmGray,
    fontWeight: 500,
  },
  productMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '2px',
  },
  metaItem: {
    fontSize: '11px',
    color: warmGray,
    backgroundColor: ghostWhite,
    borderRadius: '4px',
    padding: '2px 6px',
  },
  mfrSkuRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px',
  },
  mfrSkuItem: {
    fontSize: '11px',
    fontWeight: 600,
    color: softPurple,
    backgroundColor: ghostWhite,
    borderRadius: '4px',
    padding: '2px 6px',
  },
  upbTag: {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.4px',
    color: vividOrange,
    backgroundColor: softAmberLight,
    borderRadius: '4px',
    padding: '2px 6px',
    textTransform: 'uppercase',
  },
});

DistributorProductCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    global_product_id: PropTypes.number,
    name: PropTypes.string,
    vendor_sku: PropTypes.string,
    mfr_sku: PropTypes.string,
    pack_size: PropTypes.string,
    brand_name: PropTypes.string,
    manufacturer_name: PropTypes.string,
    vendor_name: PropTypes.string,
    thumbnail_url: PropTypes.string,
    product_gmv: PropTypes.number,
  }).isRequired,
};

export default DistributorProductCard;
